import React, { createContext, useMemo } from 'react';
import { node } from 'prop-types';

import MESSAGES from '../config/messages.json';
import { usePromoCart } from '../hooks/usePromoCart';
import { usePromoModel } from '../hooks/usePromoModel';
import { usePromoPresentation } from '../hooks/usePromoPresentation';
import {
  BACKWARDS_BOGO_SUBEXPERIENCE_TAGS,
  BMSM_SUBEXPERIENCE_TAGS,
  DOLLAR_THRESHOLD_BOGO_SUBEXPERIENCE_TAGS,
  FORWARDS_BOGO_SUBEXPERIENCE_TAGS,
  MESSAGE_STATE,
  MSB_SUBEXPERIENCE_TAGS
} from '../utils/constants';
import { toWord } from '../utils/promo-utils';
import { formatPrice, formattedReward } from '../utils/promo-presentation-utils';

/*
 * MESSAGES is a map of strings that the messages within the drawer can pull from. Dynamic values can be inserted
 * by writing in the format `$varName`.
 *
 * Current list of known variables:
 *
 * - currentAmount
 * - expDay
 * - expMonth
 * - maxRewardAmount
 * - qualifyingAmount
 * - remainingAmount
 * - rewardAmount
 * - src1ListCount
 * - tgt1ListCount
 * - totalRewardAmount
 *
 * Escape characters can also be used to apply certain styling:
 * - `\n` new line
 * - `\u00a0` non breaking space
 */

const DEFAULT_PROMO_MESSAGE_CONTEXT = Object.freeze({ messages: MESSAGES });

export const PromoMessageContext = createContext(DEFAULT_PROMO_MESSAGE_CONTEXT);

export const PromoMessageProvider = ({ children }) => {
  const {
    anchorItem,
    bogoMaxQuantity,
    displayableFirstListItemIds,
    displayableSecondListItemIds,
    expDate,
    isAnchorFulfillable,
    isBatchComplete,
    listHasFulfillment,
    refinedNvalue,
    rewardTiers,
    src1EligibilityCriteria,
    tgt1EligibilityCriteria,
    subExperience
  } = usePromoModel();
  const {
    currentPromoTier,
    isPromoAchieved,
    promoCartQuantity,
    promoCartValue,
    promoSuccessLevel
  } = usePromoCart();
  const { isRewardEnabled } = usePromoPresentation();

  const messageState = useMemo(() => {
    switch (subExperience) {
    case FORWARDS_BOGO_SUBEXPERIENCE_TAGS.buyOneGetOne:
    case FORWARDS_BOGO_SUBEXPERIENCE_TAGS.buyOneGetPercentageOff:
    case FORWARDS_BOGO_SUBEXPERIENCE_TAGS.buyOneGetDollarOff:
    case BACKWARDS_BOGO_SUBEXPERIENCE_TAGS.buyOneGetOne:
    case BACKWARDS_BOGO_SUBEXPERIENCE_TAGS.buyOneGetPercentageOff:
    case BACKWARDS_BOGO_SUBEXPERIENCE_TAGS.buyOneGetDollarOff: {
      if (!!anchorItem?.fulfillment && !isAnchorFulfillable) {
        return MESSAGE_STATE.anchorItemUnfulfillable;
      }

      if (isPromoAchieved || isRewardEnabled || promoSuccessLevel === 'success') {
        return MESSAGE_STATE.success;
      }

      break;
    }
    case DOLLAR_THRESHOLD_BOGO_SUBEXPERIENCE_TAGS.buyMinAmountGetOne:
    case DOLLAR_THRESHOLD_BOGO_SUBEXPERIENCE_TAGS.buyMinAmountGetPercentageOff:
    case DOLLAR_THRESHOLD_BOGO_SUBEXPERIENCE_TAGS.buyMinAmountGetDollarOff: {
      return isPromoAchieved ? MESSAGE_STATE.success : MESSAGE_STATE.initial;
    }
    default: break;
    }

    if (!!anchorItem?.fulfillment && !isAnchorFulfillable) {
      return MESSAGE_STATE.anchorItemUnfulfillable;
    }

    if (isBatchComplete && !listHasFulfillment) {
      return MESSAGE_STATE.src1ListUnfulfillable;
    }

    if (isPromoAchieved || promoSuccessLevel === 'success') {
      return MESSAGE_STATE.success;
    }

    if (isRewardEnabled || promoSuccessLevel === 'almost') {
      return MESSAGE_STATE.almost;
    }

    return MESSAGE_STATE.initial;
  }, [
    anchorItem,
    isAnchorFulfillable,
    isBatchComplete,
    isPromoAchieved,
    isRewardEnabled,
    listHasFulfillment,
    promoSuccessLevel,
    subExperience
  ]);

  // The names of the keys matter for this object. The message format function uses the key names
  // to determine where in the string it needs to replace. Be careful if you change any property names here.
  const messageReplaceValues = useMemo(() => {
    const expMonth = expDate?.month || null;
    const expDay = expDate?.day || null;
    let currentAmount = null;
    let maxRewardAmount = null;
    let qualifyingAmount = null;
    let remainingAmount = null;
    let rewardAmount = null;
    let src1ListCount = null;
    let tgt1ListCount = null;
    let totalRewardAmount = null;

    const {
      maxAllowedRewardAmount,
      minPurchaseQuantity,
      minPurchaseAmount,
      rewardAmountPerOrder,
      rewardAmountPerItem,
      rewardFixedPrice,
      rewardPercent
    } = rewardTiers[currentPromoTier];

    if (refinedNvalue) {
      src1ListCount = '1000+';
    } else if (displayableFirstListItemIds.length > 50) {
      src1ListCount = '50+';
    } else if (displayableFirstListItemIds.length) {
      src1ListCount = `${displayableFirstListItemIds.length}`;
    }

    if (displayableSecondListItemIds.length > 50) {
      tgt1ListCount = '50+';
    } else if (displayableSecondListItemIds.length) {
      tgt1ListCount = `${displayableSecondListItemIds.length}`;
    }

    switch (subExperience) {
    case FORWARDS_BOGO_SUBEXPERIENCE_TAGS.buyOneGetOne:
    case FORWARDS_BOGO_SUBEXPERIENCE_TAGS.buyOneGetY:
    case BACKWARDS_BOGO_SUBEXPERIENCE_TAGS.buyOneGetOne: {
      maxRewardAmount = bogoMaxQuantity;
      break;
    }
    case FORWARDS_BOGO_SUBEXPERIENCE_TAGS.buyOneGetPercentageOff:
    case FORWARDS_BOGO_SUBEXPERIENCE_TAGS.buyOneGetYPercentageOff:
    case BACKWARDS_BOGO_SUBEXPERIENCE_TAGS.buyOneGetPercentageOff: {
      rewardAmount = `${rewardPercent}%`;
      maxRewardAmount = bogoMaxQuantity;
      break;
    }
    case FORWARDS_BOGO_SUBEXPERIENCE_TAGS.buyOneGetDollarOff:
    case FORWARDS_BOGO_SUBEXPERIENCE_TAGS.buyOneGetYDollarOff:
    case BACKWARDS_BOGO_SUBEXPERIENCE_TAGS.buyOneGetDollarOff: {
      rewardAmount = formattedReward({ rewardAmountPerOrder }, true);
      maxRewardAmount = bogoMaxQuantity;
      break;
    }
    case DOLLAR_THRESHOLD_BOGO_SUBEXPERIENCE_TAGS.buyMinAmountGetOne: {
      currentAmount = formatPrice(promoCartValue, true);
      qualifyingAmount = formatPrice(src1EligibilityCriteria.minPurchaseAmount, true);
      remainingAmount = formatPrice(src1EligibilityCriteria.minPurchaseAmount - promoCartValue, true);
      maxRewardAmount = formatPrice(maxAllowedRewardAmount, true);
      break;
    }
    case DOLLAR_THRESHOLD_BOGO_SUBEXPERIENCE_TAGS.buyMinAmountGetPercentageOff: {
      currentAmount = formatPrice(promoCartValue, true);
      qualifyingAmount = formatPrice(src1EligibilityCriteria.minPurchaseAmount, true);
      remainingAmount = formatPrice(src1EligibilityCriteria.minPurchaseAmount - promoCartValue, true);
      rewardAmount = `${rewardPercent}%`;
      maxRewardAmount = formatPrice(maxAllowedRewardAmount, true);
      break;
    }
    case DOLLAR_THRESHOLD_BOGO_SUBEXPERIENCE_TAGS.buyMinAmountGetDollarOff: {
      currentAmount = formatPrice(promoCartValue, true);
      qualifyingAmount = formatPrice(src1EligibilityCriteria.minPurchaseAmount, true);
      remainingAmount = formatPrice(src1EligibilityCriteria.minPurchaseAmount - promoCartValue, true);
      rewardAmount = formattedReward({ rewardAmountPerOrder }, true);
      maxRewardAmount = formatPrice(maxAllowedRewardAmount, true);
      break;
    }
    case MSB_SUBEXPERIENCE_TAGS.MSB1: {
      currentAmount = promoCartQuantity;
      qualifyingAmount = minPurchaseQuantity;
      remainingAmount = Math.max(0, minPurchaseQuantity - promoCartQuantity);
      rewardAmount = `${rewardPercent}%`;
      break;
    }
    case MSB_SUBEXPERIENCE_TAGS.MSB2: {
      currentAmount = promoCartQuantity;
      qualifyingAmount = minPurchaseQuantity;
      remainingAmount = Math.max(0, minPurchaseQuantity - promoCartQuantity);
      rewardAmount = formatPrice(rewardAmountPerOrder);
      break;
    }
    case MSB_SUBEXPERIENCE_TAGS.MSB3: {
      currentAmount = promoCartQuantity;
      qualifyingAmount = minPurchaseQuantity;
      remainingAmount = Math.max(0, minPurchaseQuantity - promoCartQuantity);
      rewardAmount = formatPrice(rewardAmountPerItem);
      totalRewardAmount = formatPrice(rewardAmountPerItem * promoCartQuantity);
      break;
    }
    case MSB_SUBEXPERIENCE_TAGS.MSB4: {
      currentAmount = promoCartQuantity;
      qualifyingAmount = formatPrice(minPurchaseAmount);
      remainingAmount = formatPrice(Math.max(0, minPurchaseAmount - promoCartValue), true);
      rewardAmount = `${rewardPercent}%`;
      break;
    }
    case MSB_SUBEXPERIENCE_TAGS.MSB5: {
      currentAmount = promoCartQuantity;
      qualifyingAmount = formatPrice(minPurchaseAmount);
      remainingAmount = formatPrice(Math.max(0, minPurchaseAmount - promoCartValue), true);
      rewardAmount = formatPrice(rewardAmountPerOrder);
      break;
    }
    case MSB_SUBEXPERIENCE_TAGS.MSB6: {
      currentAmount = promoCartQuantity;
      qualifyingAmount = formatPrice(minPurchaseAmount);
      remainingAmount = formatPrice(Math.max(0, minPurchaseAmount - promoCartValue), true);
      rewardAmount = formatPrice(rewardAmountPerItem);
      totalRewardAmount = formatPrice(rewardAmountPerItem * promoCartQuantity);
      break;
    }
    case BMSM_SUBEXPERIENCE_TAGS.BMSM1:
    case BMSM_SUBEXPERIENCE_TAGS.BMSM2:
    case BMSM_SUBEXPERIENCE_TAGS.BMSM7: {
      currentAmount = promoCartQuantity;
      remainingAmount = Math.max(0, minPurchaseQuantity - promoCartQuantity);
      rewardAmount = formattedReward({ rewardAmountPerOrder, rewardPercent, rewardFixedPrice }, true);
      break;
    }
    case BMSM_SUBEXPERIENCE_TAGS.BMSM4:
    case BMSM_SUBEXPERIENCE_TAGS.BMSM5: {
      currentAmount = formatPrice(promoCartValue, true);
      remainingAmount = formatPrice(Math.max(0, minPurchaseAmount - promoCartValue), true);
      rewardAmount = formattedReward({ rewardAmountPerOrder, rewardPercent, rewardFixedPrice }, true);
      break;
    }
    default: {
      break;
    }
    }

    return Object.freeze({
      currentAmount,
      expMonth,
      expDay,
      maxRewardAmount,
      qualifyingAmount,
      remainingAmount,
      rewardAmount,
      src1ListCount,
      tgt1ListCount,
      src1MinPurchaseQuantity: src1EligibilityCriteria.minPurchaseQuantity || null,
      src1MinPurchaseQuantityWord: toWord(src1EligibilityCriteria.minPurchaseQuantity) || null,
      tgt1MinPurchaseQuantity: tgt1EligibilityCriteria.minPurchaseQuantity || null,
      tgt1MinPurchaseQuantityWord: toWord(tgt1EligibilityCriteria.minPurchaseQuantity) || null,
      totalRewardAmount
    });
  }, [
    bogoMaxQuantity,
    currentPromoTier,
    displayableFirstListItemIds?.length,
    displayableSecondListItemIds?.length,
    expDate?.day,
    expDate?.month,
    promoCartQuantity,
    promoCartValue,
    refinedNvalue,
    rewardTiers,
    src1EligibilityCriteria.minPurchaseAmount,
    src1EligibilityCriteria.minPurchaseQuantity,
    tgt1EligibilityCriteria.minPurchaseQuantity,
    subExperience
  ]);

  const contextValue = useMemo(() => {
    return Object.freeze({
      messageReplaceValues,
      messages: MESSAGES,
      messageState,
    });
  }, [messageReplaceValues, messageState]);

  return (
    <PromoMessageContext.Provider value={contextValue}>
      {children}
    </PromoMessageContext.Provider>
  );
};

PromoMessageProvider.propTypes = {
  children: node.isRequired
};
